import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import type { GridColDef } from '@mui/x-data-grid';
import { Checkbox, ChipInput, SelectableCreativesGrid } from 'components/common';
import type { ReuploadCreativeRef } from 'features/creatives';
import datadogActions from 'lib/datadog';
import type { CreativeEntry } from 'types/shared';
import { Network } from '../../../../constants';
import { UPLOAD_CREATIVES_LIMIT } from '../constants';
import { useUploadCreativesRows } from '../hooks';
import { toReuploadCreativeRefs } from '../utils';

const COLUMNS: GridColDef<CreativeEntry>[] = [
  { field: 'creative_id', headerName: 'Creative ID', width: 110 },
  { field: 'name', headerName: 'Name', width: 450 },
  { field: 'app', headerName: 'App', width: 90 },
  { field: 'creative_type', headerName: 'Type', width: 100 },
  { field: 'created', headerName: 'Created', width: 120 },
];

interface AddCreativesFormValues {
  filterCreativeByName: string[];
  selectedCreativesOnly: boolean;
  selectedCreatives: string[];
}

interface AddCreativesDialogProps {
  open: boolean;
  app?: string;
  isUploading: boolean;
  onClose: () => void;
  onUpload: (refs: ReuploadCreativeRef[]) => void;
}

type PickerProps = Omit<AddCreativesDialogProps, 'open'>;

const AddCreativesPicker: FC<PickerProps> = ({ app, isUploading, onClose, onUpload }) => {
  const { rows, selectedRows, totalCount, selectedCreatives, setSelection, isLoading, isError, error } =
    useUploadCreativesRows(app);
  const creativeRefs = useMemo(() => toReuploadCreativeRefs(selectedRows), [selectedRows]);

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, {
      source: 'twitter_upload_creatives_creatives_query',
      network: Network.X,
    });
  }, [isError, error]);

  return (
    <>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
          <Box sx={{ flex: 1, maxWidth: '50%' }}>
            <ChipInput name="filterCreativeByName" inputLabel="Search by name" />
          </Box>
          <Checkbox
            dataTestId="selectedCreativesOnly"
            name="selectedCreativesOnly"
            label="Selected only"
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        {!app ? (
          <Typography variant="body2" color="text.secondary">
            Select an application to see creatives
          </Typography>
        ) : null}
        <SelectableCreativesGrid
          rows={rows}
          columns={COLUMNS}
          selectedIds={selectedCreatives}
          onSelectionChange={setSelection}
          limit={UPLOAD_CREATIVES_LIMIT}
          isLoading={isLoading}
          totalCount={totalCount}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!creativeRefs.length || isUploading}
          onClick={() => {
            onUpload(creativeRefs);
          }}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Upload creatives
        </Button>
      </DialogActions>
    </>
  );
};

// The form lives here so it resets every time the dialog closes and unmounts.
const AddCreativesForm: FC<PickerProps> = (props) => {
  const formMethods = useForm<AddCreativesFormValues>({
    defaultValues: { filterCreativeByName: [], selectedCreativesOnly: false, selectedCreatives: [] },
  });

  return (
    <FormProvider {...formMethods}>
      <AddCreativesPicker {...props} />
    </FormProvider>
  );
};

const AddCreativesDialog: FC<AddCreativesDialogProps> = ({ open, onClose, ...pickerProps }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: '85vh' } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
      Add creatives
      <IconButton aria-label="Close" onClick={onClose}>
        <Close />
      </IconButton>
    </DialogTitle>
    <AddCreativesForm onClose={onClose} {...pickerProps} />
  </Dialog>
);

export default AddCreativesDialog;
